import React from 'react';
import useMarketingModule from '@hooks/useMarketingModuleLogic';
import Heading from '@solar/common/Heading/Heading';
import Link from 'next/link';
import { getCdnImageUrl } from '@utils/cloudflare';
import useIsMobile from '@hooks/useIsMobile';
import Image from 'next/image';
import withPromoLogic, { PromoLogicProps } from '../CaptureEmail/withPromoLogic';

const promoConfig = {
  original: {
    image: 'marketing-module/web-homepage-marketing-module-desktop.jpg',
    mobile_image: 'marketing-module/web-homepage-marketing-module-mobile.jpg',
    pre_submission_title: "It's Officially Pool Season. Be the First One In.",
    pre_submission_subtitle:
      'Private cabana? Cozy daybed? Book your spot, skip the crowds, and claim your best day yet—poolside perks included.',
    pre_submission_button_label: 'Sign Up',
    post_submission_title: 'Confirmed',
    post_submission_subtitle: 'Check your inbox for weekly updates and inspiration.',
    already_submitted_title: 'Recharge and reset',
    already_submitted_subtitle:
      "Today's the perfect day to book a relaxing and memorable daycation at a luxury pool or spa.",
    already_submitted_button_label: 'Explore',
  },
};

function HomePageMarketingModule({ handleClick }: PromoLogicProps) {
  const { currentStep, email, handleCTAClick, handleSubmit, setEmail } =
    useMarketingModule(handleClick);
  const isMobile = useIsMobile({ isSolarSystem: true });

  return (
    <div className="bg-solar-surface-brand-shadow flex flex-col items-center justify-center pb-10 pt-172px relative rounded-xl w-full d:flex-row d:pb-50px d:pt-58px d:h-370">
      <div className="absolute h-180px -top-32px w-233px d:relative d:-top-1 d:w-544px d:h-420px">
        <Image
          className="rounded-xl"
          src={getCdnImageUrl(
            500,
            isMobile ? promoConfig.original.mobile_image : promoConfig.original.image,
            'https://static-assets.resortpass.com/assets/',
            100,
          )}
          alt={promoConfig.original.pre_submission_title || ''}
          title={promoConfig.original.pre_submission_title || ''}
          loading="eager"
          layout="fill"
          objectFit="cover"
          quality={100}
        />
      </div>

      <div className="flex flex-col max-w-624px ml-0 px-spacing-lg text-solar-surface-secondary d:px-0 d:ml-11 d:pr-11 w-full">
        <Heading level={2} className="max-w-295 d:w-full d:max-w-360">
          {currentStep === 1 && promoConfig.original.pre_submission_title}
          {currentStep === 2 && promoConfig.original.post_submission_title}
          {currentStep === 3 && promoConfig.original.already_submitted_title}
        </Heading>

        <Heading level={5} className="flex flex-col mt-spacing-md mb-spacing-lg d:mb-spacing-xl">
          {currentStep === 1 && promoConfig.original.pre_submission_subtitle}
          {currentStep === 2 && promoConfig.original.post_submission_subtitle}
          {currentStep === 3 && promoConfig.original.already_submitted_subtitle}

          {currentStep === 1 ? (
            <Link href="/terms-conditions" passHref>
              <a target="_blank" className="hover:underline">
                Terms and Conditions apply.
              </a>
            </Link>
          ) : null}
        </Heading>

        {currentStep === 1 && (
          <div className="bg-solar-surface-primary flex justify-between rounded-4 border border-rp-gray-divider p-2 d:w-90">
            <input
              className="mr-4 outline-none text-base text-solar-secondary w-full placeholder-text-solar-secondary"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
            />

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="bg-solar-surface-brand-shadow text-solar-inverted-primary flex-shrink-0 font-rp-pn-semi-bold h-9 text-base rounded-4 w-93px"
            >
              {promoConfig.original.pre_submission_button_label}
            </button>
          </div>
        )}

        {currentStep === 3 && promoConfig.original.already_submitted_button_label && (
          <button
            type="button"
            onClick={handleCTAClick}
            className="font-rp-basetica bg-solar-button-secondary-default capitalize text-base border border-solar-button rounded-4 h-11 text-solar-primary w-full d:w-185px"
          >
            {promoConfig.original.already_submitted_button_label}
          </button>
        )}
      </div>
    </div>
  );
}

export default withPromoLogic(HomePageMarketingModule, 'HomePageMarketingModule');
